import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";

interface PermissionGuardProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requiredAnyPermissions?: string[];
  requiredAllPermissions?: string[];
  requiredRole?: string;
  fallback?: React.ReactNode;
  showForbiddenMessage?: boolean;
  hideInsteadOfRedirect?: boolean;
  mode?: "hide" | "disable" | "forbidden";
  showTooltip?: boolean;
  tooltipMessage?: string;
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  requiredPermissions = [],
  requiredAnyPermissions = [],
  requiredAllPermissions = [],
  requiredRole,
  fallback = null,
  showForbiddenMessage = false,
  hideInsteadOfRedirect = false,
  mode = "hide",
  showTooltip = false,
  tooltipMessage = "You don't have permission to access this content",
}) => {
  const { userProfile, isAuthLoading } = useAuth();
  const { can, canAny, canAll } = usePermissions();

  // Show loading while authentication is being determined
  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no profile data, show fallback
  if (!userProfile) {
    return <>{fallback}</>;
  }

  // Check role requirement
  if (requiredRole && userProfile.role?.name !== requiredRole) {
    if (mode === "hide" || hideInsteadOfRedirect) {
      return null;
    }
    if (mode === "disable") {
      return (
        <div
          className="opacity-50 pointer-events-none"
          title={showTooltip ? tooltipMessage : undefined}
        >
          {children}
        </div>
      );
    }
    return <>{fallback}</>;
  }

  // Check individual permissions
  const hasRequiredPermissions = requiredPermissions.every((permission) =>
    can(permission)
  );

  // Check any permissions (OR logic)
  const hasAnyRequiredPermissions =
    requiredAnyPermissions.length === 0 || canAny(requiredAnyPermissions);

  // Check all permissions (AND logic)
  const hasAllRequiredPermissions =
    requiredAllPermissions.length === 0 || canAll(requiredAllPermissions);

  // Determine if user has access
  const hasAccess =
    hasRequiredPermissions &&
    hasAnyRequiredPermissions &&
    hasAllRequiredPermissions;

  // Handle different modes when user doesn't have access
  if (!hasAccess) {
    if (mode === "hide" || hideInsteadOfRedirect) {
      return null;
    }

    if (mode === "disable") {
      return (
        <div
          className="opacity-50 pointer-events-none"
          title={showTooltip ? tooltipMessage : undefined}
        >
          {children}
        </div>
      );
    }

    if (mode === "forbidden" || showForbiddenMessage) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Access Forbidden
            </h1>
            <p className="text-gray-600 mb-4">{tooltipMessage}</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return <>{fallback}</>;
  }

  // User has access, render children
  return <>{children}</>;
};

export default PermissionGuard;
